import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Drawer } from 'antd';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import { DateTimePickerInput } from 'v3/Components/Form/DateTimePickers/DateTimePicker';
import Dropdown, { DropdownOption, isOptionEmpty } from 'v3/Components/Form/Dropdown';
import TextInput from 'v3/Components/Form/TextInput';
import FileUploadBox, { UploadedFile } from 'v3/Components/Form/FileUploadBox';
import { PrimaryButton, TertiaryButton } from 'v3/Components/Shared/Buttons';
import { useEnums, useGlobalState, useToasts } from 'Context';
import { buildEndpoint, useDataPoster, useHasPermissionsV3 } from 'Utilities';
import { ENDPOINTS, METHODS } from 'Models/Templates/Routes/Routes';
import { NamedCoreEnum } from 'Models';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import { isPastRawDate } from 'Utilities/DateTimeUtc';
import { useSetFieldValueTouched } from 'v3/Components/Form/useSetFieldValueTouched';
import { StateLicense } from './ReviewerProfile.Overview.LicenseInformation';

const EMPTY_OPTION: DropdownOption = { value: '', displayName: '' };

// Deprecated for reviewer state licenses only — the underlying State enumeration is shared
// app-wide (e.g. "State of Jurisdiction"/BenefitState dropdowns still need Texas Non-HCN), so
// this filters the license "State" dropdown's options only, not the enumeration endpoint itself.
const EXCLUDED_LICENSE_STATE_CODES = ['AS', 'GU', 'MP', 'TC', 'UM'];

const filterStateEnum = (stateEnum: NamedCoreEnum): NamedCoreEnum =>
  Object.keys(stateEnum)
    .filter((code) => !EXCLUDED_LICENSE_STATE_CODES.includes(code))
    .reduce((acc, code) => ({ ...acc, [code]: stateEnum[code] }), {} as NamedCoreEnum);

type FormValues = {
  expirationDate: string;
  state: DropdownOption;
  licenseNumber: string;
  npiNumber: string;
};

const INITIAL_VALUES: FormValues = {
  expirationDate: '',
  state: EMPTY_OPTION,
  licenseNumber: '',
  npiNumber: '',
};

type Props = {
  mode: 'add' | 'update';
  reviewerId: string;
  license?: StateLicense;
  open: boolean;
  onClose: () => void;
  onSuccess: (license: StateLicense) => void;
};

export const StateLicenseDrawer: React.FC<Props> = ({
  mode,
  reviewerId,
  license,
  open,
  onClose,
  onSuccess,
}) => {
  const toast = useToasts();
  const { state: stateEnum } = useEnums();
  const licenseStateEnum = useMemo(() => filterStateEnum(stateEnum), [stateEnum]);
  const { baseUrl } = useGlobalState();
  const isUpdate = mode === 'update';
  const hasManagePrivileged = useHasPermissionsV3()(PermissionCodesV3.Reviewer.License.ManagePrivileged);

  const [saving, setSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);

  const formik = useFormik<FormValues>({
    initialValues: INITIAL_VALUES,
    validate: (values) => {
      const errors: Partial<Record<keyof FormValues, string>> = {};
      // Privileged-only fields are disabled and omitted from the payload on update by a
      // non-privileged user (see handleSave), so they aren't required in that case either.
      const privilegedFieldsRequired = !isUpdate || hasManagePrivileged;

      if (!values.expirationDate) {
        errors.expirationDate = 'Expiration Date is required';
      }

      if (privilegedFieldsRequired) {
        if (isOptionEmpty(values.state)) {
          errors.state = 'State is required';
        }

        if (!values.licenseNumber) {
          errors.licenseNumber = 'License # is required';
        }

        if (!values.npiNumber) {
          errors.npiNumber = 'NPI # is required';
        }
      }

      if (values.licenseNumber && values.licenseNumber.length > 20) {
        errors.licenseNumber = 'License # must be 20 characters or fewer';
      } else if (values.licenseNumber && !/^[A-Za-z0-9-]*$/.test(values.licenseNumber)) {
        errors.licenseNumber = 'License # can only contain letters, numbers, and hyphens';
      }

      if (values.npiNumber && !/^\d{10}$/.test(values.npiNumber)) {
        errors.npiNumber = 'NPI # must be exactly 10 digits';
      }

      return errors;
    },
    onSubmit: () => {},
  });

  // Reset / populate form when drawer opens or license changes
  useEffect(() => {
    if (!open) return;

    // Sync upload state with the license's existing file
    if (isUpdate && license?.fileName) {
      setUploadedFile({ serverId: license.fileId ?? '', filename: license.fileName, fileSize: 0 });
    } else {
      setUploadedFile(null);
    }

    if (isUpdate && license) {
      formik.setValues({
        expirationDate: license.expirationDate ?? '',
        state: license.state ? { value: license.state, displayName: license.state } : EMPTY_OPTION,
        licenseNumber: license.licenseNumber ?? '',
        npiNumber: license.npiNumber ?? '',
      });
    } else {
      formik.resetForm();
    }
  }, [open, license]);

  const setFieldValueTouched = useSetFieldValueTouched(formik);

  // Full upload URL — uses fetch so the browser sets Content-Type: multipart/form-data
  // with the correct boundary (axios instance headers would inject application/json otherwise)
  const uploadEndpoint = `${baseUrl}api/${buildEndpoint(ENDPOINTS.reviewerLicenseDocuments.base, METHODS.Upload)}`;

  const [postAdd] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'stateLicenses'),
    undefined,
    false,
    '3.0'
  );

  const [postUpdate] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'stateLicenses', license?.id),
    undefined,
    true,
    '3.0'
  );

  const handleSave = async () => {
    if (isUploading) return;

    setSaving(true);
    try {
      const fileId = uploadedFile?.serverId ?? '';
      const fileName = uploadedFile?.filename ?? '';
      const size = uploadedFile?.fileSize ?? 0;

      const { expirationDate, state, licenseNumber, npiNumber } = formik.values;

      let resp: any;
      if (isUpdate) {
        resp = await postUpdate({
          expirationDate: expirationDate || null,
          ...(hasManagePrivileged && {
            state: state.value,
            licenseNumber: licenseNumber.trim(),
            npiNumber: npiNumber.trim(),
          }),
          fileId,
          fileName,
          size,
        });
      } else {
        resp = await postAdd({
          expirationDate: expirationDate || null,
          state: state.value,
          licenseNumber: licenseNumber.trim(),
          npiNumber: npiNumber.trim(),
          fileId,
          fileName,
          size,
        });
      }

      if (resp?.status === 200 || resp?.status === 201) {
        const returnedLicense: StateLicense = {
          id: resp.data?.id ?? license?.id ?? '',
          expirationDate: resp.data?.expirationDate ?? expirationDate,
          isExpired: resp.data?.isExpired ?? false,
          state: resp.data?.state ?? String(state.value ?? ''),
          licenseNumber: resp.data?.licenseNumber ?? licenseNumber,
          school: resp.data?.school,
          npiNumber: resp.data?.npiNumber ?? npiNumber,
          fileName: fileName || undefined,
        };
        onSuccess(returnedLicense);
      } else if (resp?.response?.status === 400) {
        const data = resp.response?.data;
        if (data?.errors) {
          const mappedErrors: Record<string, string> = {};
          Object.entries(data.errors).forEach(([key, val]) => {
            const field = key.charAt(0).toLowerCase() + key.slice(1);
            mappedErrors[field] = (val as string[])[0];
          });
          formik.setErrors(mappedErrors);
          formik.setTouched(Object.keys(mappedErrors).reduce((acc, k) => ({ ...acc, [k]: true }), {}));
        }
        toast.error('Please validate the highlighted fields');
      } else if (resp?.response?.status === 403) {
        toast.error(resp.response?.data?.message ?? 'You do not have permission to perform this action.');
      } else {
        // 5xx or no response at all (network failure / blocked request).
        toast.error('Something went wrong');
      }
    } finally {
      setSaving(false);
    }
  };

  const drawerTitle = (
    <span className="sl-drawer-title">
      {isUpdate ? 'Update State License' : 'Add State License'}
    </span>
  );

  const footer = (
    <div className="sl-drawer-footer">
      <TertiaryButton title="Cancel" onClick={onClose} />
      <PrimaryButton
        title="Save"
        iconPosition="start"
        onClick={handleSave}
        disabled={saving || isUploading}
      >
        {saving && (
          <CircularProgress size={16} thickness={5} className="sl-drawer-save-spinner" />
        )}
      </PrimaryButton>
    </div>
  );

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      title={drawerTitle}
      width={720}
      closable
      closeIcon={<CloseIcon />}
      footer={footer}
      destroyOnClose
      className="sl-drawer"
    >
      <div className="state-license-drawer-form">
        <div className="sl-drawer-fields-section">
          <p className="sl-drawer-description">
            Please provide the correct details on the reviewer's licensing.
          </p>

          {/* 2-column grid: left col 322px fixed, right col flexible */}
          <div className="sl-drawer-fields-grid">
          {/* Row 1: Expiration Date | State */}
          <DateTimePickerInput
            label="Expiration Date"
            required
            value={formik.values.expirationDate}
            onChange={(val: string) => setFieldValueTouched('expirationDate', val)}
            error={formik.touched.expirationDate ? formik.errors.expirationDate : undefined}
            hint={isPastRawDate(formik.values.expirationDate) ? 'This expiration date is in the past' : undefined}
            dateOnly
          />
          <Dropdown
            label="State"
            required
            enums={licenseStateEnum}
            value={formik.values.state}
            onChange={(opt) => setFieldValueTouched('state', opt)}
            error={formik.touched.state ? (formik.errors.state as string) : undefined}
            showIcons={false}
            sortAlphabetically
            disabled={isUpdate && !hasManagePrivileged}
          />

          {/* Row 2: License # | NPI # */}
          <TextInput
            label="License #"
            required
            value={formik.values.licenseNumber}
            onChange={(e) => setFieldValueTouched('licenseNumber', e.target.value)}
            error={formik.touched.licenseNumber ? formik.errors.licenseNumber : undefined}
            maxLength={20}
            disabled={isUpdate && !hasManagePrivileged}
          />
          <TextInput
            label="NPI #"
            required
            value={formik.values.npiNumber}
            onChange={(e) => setFieldValueTouched('npiNumber', e.target.value)}
            error={formik.touched.npiNumber ? formik.errors.npiNumber : undefined}
            maxLength={10}
            disabled={isUpdate && !hasManagePrivileged}
          />
          </div>
        </div>

        <FileUploadBox
          uploadEndpoint={uploadEndpoint}
          initialFile={
            isUpdate && license?.fileName
              ? { serverId: license.fileId ?? '', filename: license.fileName, fileSize: 0 }
              : undefined
          }
          label="Proof of License"
          note="This license will be uploaded to reviewer's documents with the tag: License"
          onChange={(f) => setUploadedFile(f)}
          onUploadStateChange={(s) => setIsUploading(s === 'uploading')}
        />
      </div>
    </Drawer>
  );
};
